import React from "react";
import yup from "yup";
import { useObserver } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { useFormStore } from "@modules/formx-react/useFormStore";
import RegularButton from "@components/buttons/RegularButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import locale from "@bonliva-crm/locale";
import { Select } from "@common/formx_item_form";
import { FormxDebugger, Form, ToggleButtonGroup } from "@common/formx";
import { sickOrVabTypes, weekDays } from "./utils";
import { PeriodRow } from "./PeriodRow";

type Props = {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Omit<BasicPeriodFormData, "type">) => void;
  maxDayShifts: number;
  isSickOrVab?: boolean;
  initialValues?: BasicPeriod[];
};

type BasicPeriod = {
  from: string;
  to: string;
  type?: string;
};

export type BasicPeriodFormData = {
  weekDays: number[];
  periods: BasicPeriod[];
  type?: string;
};

const getSchema = (isSickOrVab = false) => {
  const typeScheme = yup.string().nullable(true);

  return yup.object<BasicPeriodFormData>({
    weekDays: yup.array().of(yup.number()),
    periods: yup.array().of(
      yup.object<BasicPeriod>({
        from: yup.string().required(locale.Required),
        to: yup.string().required(locale.Required)
      })
    ),
    type: isSickOrVab ? typeScheme.required() : typeScheme
  });
};

export function BasicPeriodForm(props: Props) {
  const classes = useStyles();

  const initialPeriods = props.initialValues?.length
    ? props.initialValues
    : [{ from: "", to: "", type: null }];

  const formStore = useFormStore({
    schema: getSchema(props.isSickOrVab),
    initialValues: {
      weekDays: [],
      periods: initialPeriods,
      type: null
    },
    initialDirty: false,
    onSubmit: async () => {
      const values = formStore.values;

      const periods = props.isSickOrVab
        ? values.periods.map((x) => ({
            ...x,
            type: values.type
          }))
        : values.periods;

      props.onSubmit({
        weekDays: values.weekDays,
        periods
      });
    }
  });

  const setPeriodByIndex = (index: number, from: string, to: string) => {
    formStore.setValue(`periods.${index}.from`, from);
    formStore.setValue(`periods.${index}.to`, to);
  };

  return useObserver(() => {
    const periods = formStore.values.periods;
    const visibleAddPeriodButton = periods.length < props.maxDayShifts;

    return (
      <Dialog open={props.open} onClose={props.onClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <div className={classes.titleWrap}>
            {locale.EditTime}: {props.title}
            {visibleAddPeriodButton && (
              <div className={classes.addBtnWrap}>
                <IconButton
                  color="default"
                  size="small"
                  aria-label="add"
                  classes={{ root: classes.addBtn }}
                  onClick={() =>
                    formStore.push("periods", {
                      from: "",
                      to: ""
                    })
                  }
                >
                  <Add fontSize="small" />
                </IconButton>
                <Typography variant="caption">Pass</Typography>
              </div>
            )}
          </div>
        </DialogTitle>

        <DialogContent>
          <Form store={formStore} allowUpdates>
            <FormxDebugger store={formStore} />

            <Grid container spacing={1}>
              {periods.map((x, index) => {
                const name = `periods.${index}`;

                return (
                  <Grid
                    item
                    xs={12}
                    style={{ paddingTop: index > 0 ? 16 : 0 }}
                    key={index}
                  >
                    <PeriodRow
                      index={index}
                      name={name}
                      isRemovable={periods.length > 1}
                      onChange={(from, to) => setPeriodByIndex(index, from, to)}
                      onRemove={() => formStore.removeByIndex("periods", index)}
                    />
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <ToggleButtonGroup
                  name="weekDays"
                  exclusive={false}
                  wrap
                  label={locale.RepeatOnWeekDays}
                  options={weekDays.map((l, i) => ({
                    label: l,
                    value: i
                  }))}
                />
              </Grid>

              {props.isSickOrVab ? (
                <Grid item xs={12}>
                  <Select
                    label={locale["Worktype"]}
                    name="type"
                    options={sickOrVabTypes.map((value) => ({
                      label: locale[value],
                      value
                    }))}
                    required
                  />
                </Grid>
              ) : null}
            </Grid>
          </Form>
        </DialogContent>

        <DialogActions>
          <RegularButton style={{ marginRight: "auto" }} onClick={props.onClose}>
            {locale.Cancel}
          </RegularButton>

          <PrimaryButton
            disabled={!formStore.canSubmit}
            onClick={() => formStore.submit()}
          >
            OK
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    );
  });
}

const useStyles = makeStyles((theme) => ({
  titleWrap: {
    position: "relative"
  },
  addBtnWrap: {
    position: "absolute",
    right: "2px",
    top: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  addBtn: {
    marginBottom: "4px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.primary.light
    }
  }
}));
